import { useState } from 'react';
import { PageWrapper } from '@src/components/Wrapper';
import { OptionSelector } from '@src/components/OptionSelector';
import { useNavigate } from 'react-router-dom';
import { ROUTE_MY_ESIMS_FULL_URL } from '@src/constants/URLS.const';
import { Button } from 'antd';
import { AndroidFilled } from '@ant-design/icons';

type TabKey = 'qr' | 'manual';

interface AndroidGuideProps {
  onClose: () => void;
}

export function AndroidGuide({ onClose }: Readonly<AndroidGuideProps>) {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'qr' | 'manual'>('qr');
  const tabOptions: TabKey[] = ['qr', 'manual'];
  const handleTabChange = (key: TabKey | null) => {
    if (!key) return;
    setActiveTab(key);
  };

  const handleInstallClick = () => {
    navigate(ROUTE_MY_ESIMS_FULL_URL);
    onClose();
  };

  return (
    <PageWrapper rootClassName='bg-wl-background p-4'>
      <div className='flex justify-between bg-wl-surface rounded-lg py-4 px-8 text-wl-on-background'>
        <span>Android</span>{' '}
        <AndroidFilled style={{ fontSize: '20px', color: '#69af69' }} />
      </div>

      {/* Tab Buttons */}
      <div className='flex justify-center gap-4 my-4'>
        <OptionSelector<TabKey>
          options={tabOptions}
          selected={activeTab}
          onSelect={handleTabChange}
          renderLabel={(key) => (key === 'qr' ? 'QR Code' : 'Manual')}
          className='w-full !pr-0 [&_button]:w-full'
        />
      </div>
      <Button
        className='btn-primary w-full mb-4  h-12'
        onClick={handleInstallClick}
      >
        Install eSIM
      </Button>
      <div className='p-4 rounded-xl bg-error-container text-error-light text-M-14'>
        WARNING! Most eSIMs can only be installed once. If you remove the eSIM
        from your device, you cannot install it again.
      </div>
      {/* QR Code Guide */}
      {activeTab === 'qr' && (
        <div className='max-w-2xl mx-auto p-6 bg-wl-surface text-wl-on-background rounded-lg mt-6'>
          <p className='text-B-16 mb-4'>QR Code Installation Method</p>
          <div className='mb-4'>
            <p className='text-B-16 mb-4'>Step 1/2 – Install eSIM</p>
            <ol className='list-decimal list-inside text-M-14 space-y-2 pl-4'>
              <li>
                Go to Settings → Network & Internet → Mobile Network → Add
                Carrier / eSIM.
              </li>
              <li>Scan the QR code from the My eSIM section.</li>
              <li>Tap Download / Activate and wait for installation.</li>
              <li>
                Set the new eSIM as your default for mobile data (if needed).
              </li>
            </ol>
          </div>
          <div>
            <p className='text-B-16 mb-4'>Step 2/2 – Access Data</p>
            <ol className='list-decimal list-inside text-M-14 space-y-2 pl-4'>
              <li>In Mobile Network settings, select your new eSIM.</li>
              <li>Toggle Enable / Use SIM to turn it on.</li>
              <li>
                (Optional – only if issues) Manually select the correct network
                under Network Selection.
              </li>
              <li>Turn Data Roaming ON to ensure connectivity.</li>
            </ol>
          </div>
        </div>
      )}

      {/* Manual Guide */}
      {activeTab === 'manual' && (
        <div className='max-w-2xl mx-auto p-6 bg-wl-surface text-wl-on-background rounded-lg mt-6 space-y-6'>
          <p className='text-B-16 mb-4'>Manual Installation Method</p>
          <div className='mb-4'>
            <p className='text-B-16 mb-4'>Step 1/2 – Install eSIM</p>
            <ol className='list-decimal list-inside text-M-14 space-y-2 pl-4'>
              <li>
                Go to: Settings → Network & Internet → Mobile Network → Add eSIM
                (or Set up SIM manually, depending on your phone model)
              </li>
              <li>
                <span>Tap: “Enter details manually” (or similar option)</span>
                <ul className='list-disc list-inside pl-6 space-y-1'>
                  <li>SM-DP+ Address</li>
                  <li>Activation Code</li>
                </ul>
              </li>
              <li>
                Tap Next, then Continue twice and wait for your phone to connect
                to the network.
              </li>
              <li>Once complete, tap Done.</li>
            </ol>
          </div>
          <div>
            <p className='text-B-16 mb-4'>Step 2/2 – Access Data</p>
            <ol className='list-decimal list-inside text-M-14 space-y-2 pl-4'>
              <li>In Mobile Network settings, select your new eSIM.</li>
              <li>Toggle Enable / Use SIM to turn it on.</li>
              <li>
                (Optional – only if issues) Manually select the correct network
                under Network Selection.
              </li>
              <li>Turn Data Roaming ON to ensure connectivity.</li>
            </ol>
          </div>
        </div>
      )}
    </PageWrapper>
  );
}
